import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get, has } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VisitorModel } from 'client/data/models/visitor';
import { PRE_VIN_PATH } from 'site-modules/financing/constants/financing';
import {
  IS_CAP_ONE_DISABLE_PARAM,
  JOINT,
  FINANCING_FF_APP_DISABLED,
} from 'site-modules/shared/constants/financing/financing-constants';
import {
  BASICS_STEP,
  LOOK_UP_STEP,
  CHOOSE_ELIGIBLE_VIN,
  ERROR_STEP,
  DEBUG_FINANCING_API,
} from 'site-modules/shared/constants/financing/steps';
import { getQuery } from 'client/utils/location';
import { getVinPath } from 'client/site-modules/financing/utils/financing-utils';
import { ErrorScreen } from 'client/site-modules/shared/components/error-screen/error-screen';
import { getConfigOptionsV2 } from 'site-modules/financing/utils/api-utils';
import { isEligible } from 'site-modules/shared/utils/financing/financing';
import { isAvailableEnvToBypassApiGateway } from 'site-modules/shared/utils/financing/is-available-env-to-bypass-api-gateway';
import { SEOHead } from 'site-modules/shared/pages/seo';
import { FinancingContainer } from 'site-modules/financing/components/financing-container/financing-container';
import { PreQualifyFinancingMockAsync } from 'site-modules/financing/components/shared/pre-qualify-financing-mock/pre-qualify-financing-mock-async';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { ErrorPageMessage } from 'site-modules/shared/components/error-page-message/error-page-message';
import { ExperimentsPanel } from 'site-modules/shared/components/experiment/panel/experiments-panel';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { Footer } from 'site-modules/shared/components/footer/footer';
import { GlobalNavigation } from 'site-modules/shared/components/global-navigation-v2/global-navigation';
import { ProfileContextAsync } from 'site-modules/shared/components/profile/profile-context-async';
import { SkipToMainContent } from 'site-modules/shared/components/skip-to-main-content/skip-to-main-content';
import { SpaLinks } from 'site-modules/shared/components/spa-links/spa-links';

export function FinancingDecoratorUI({
  match,
  children,
  vehicle = null,
  isMobile = false,
  location,
  edmundsEnv = '',
  venomEnv = '',
  isCapOneBypassApiGateway = false,
  isCapOneAppDisable = false,
  ...restProps
}) {
  let currentStep = get(match, 'params.step') || BASICS_STEP;
  const vin = get(match, 'params.vin');
  const query = getQuery(location);
  const isJoint = has(query, JOINT);
  const isLookUp = has(query, LOOK_UP_STEP);
  const chooseEligibleVin = has(query, CHOOSE_ELIGIBLE_VIN);
  const debugFinancingAPI = has(query, DEBUG_FINANCING_API);
  const isEligibleVIN = isEligible(vehicle);
  const isErrorActiveStep = get(match, 'params.step') === ERROR_STEP;
  const isPreVinFlow = location.pathname.includes(PRE_VIN_PATH);
  const isCapOneAppDisableQuery = query[IS_CAP_ONE_DISABLE_PARAM];

  const isHideCapOneApp = isCapOneAppDisableQuery ? isCapOneAppDisableQuery === 'true' : isCapOneAppDisable;

  const isAllowBypassApiGateway =
    isAvailableEnvToBypassApiGateway({ venomEnv, edmundsEnv }) && isCapOneBypassApiGateway;
  const configOptions = getConfigOptionsV2(isAllowBypassApiGateway);

  if (isLookUp) {
    currentStep = LOOK_UP_STEP;
  }

  if (!vehicle && !chooseEligibleVin && !isPreVinFlow) {
    return null;
  }

  return (
    <div className={classnames('edm-page has-global-nav', { desktop: !isMobile })}>
      <ErrorBoundary>
        <SkipToMainContent />
        <GlobalNavigation showSearchBar />
      </ErrorBoundary>
      <ErrorBoundary>
        <SEOHead
          match={match}
          vehicle={vehicle}
          isMobile={isMobile}
          location={location}
          edmundsEnv={edmundsEnv}
          venomEnv={venomEnv}
          isCapOneBypassApiGateway={isCapOneBypassApiGateway}
          isCapOneAppDisable={isCapOneAppDisable}
          {...restProps}
        >
          {children}
        </SEOHead>
      </ErrorBoundary>
      <ErrorBoundary>
        <FeatureFlag name="testSpa">
          <SpaLinks />
        </FeatureFlag>
      </ErrorBoundary>
      <ErrorBoundary Fallback={ErrorPageMessage}>
        {isHideCapOneApp ? (
          <div className="py-3 error-screen-financing">
            <ErrorScreen
              heading="We're Having Some Problems"
              subHeading={`This feature is temporarily unavailable while we resolve this issue. \n Please check back a little later`}
              creativeId={FINANCING_FF_APP_DISABLED}
            />
          </div>
        ) : (
          <Fragment>
            {chooseEligibleVin && (
              <PreQualifyFinancingMockAsync
                chooseEligibleVin={chooseEligibleVin}
                vin={vin}
                query={query}
                location={location}
              />
            )}
            {!chooseEligibleVin && (
              <FinancingContainer
                activeStep={currentStep}
                vin={vin}
                vehicle={vehicle}
                isJoint={isJoint}
                isEligibleVIN={isEligibleVIN}
                isErrorActiveStep={isErrorActiveStep}
                debugFinancingAPI={debugFinancingAPI}
                isPreVinFlow={isPreVinFlow}
                isAllowBypassApiGateway={isAllowBypassApiGateway}
                configOptions={configOptions}
              >
                {children}
              </FinancingContainer>
            )}
          </Fragment>
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        <Footer />
      </ErrorBoundary>
      <ErrorBoundary>
        <FeatureFlag name="insider">
          <ProfileContextAsync />
        </FeatureFlag>
      </ErrorBoundary>
      <ErrorBoundary>
        <ClientSideRender>
          <ExperimentsPanel />
        </ClientSideRender>
      </ErrorBoundary>
    </div>
  );
}
FinancingDecoratorUI.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  match: PropTypes.shape({ params: PropTypes.shape({ step: PropTypes.string, vin: PropTypes.string }) }).isRequired,
  vehicle: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
  venomEnv: PropTypes.string,
  edmundsEnv: PropTypes.string,
  isCapOneBypassApiGateway: PropTypes.bool,
  isCapOneAppDisable: PropTypes.bool,
};

export const stateToPropsConfig = {
  vehicle: bindToPath(({ match }) => getVinPath(match), InventoryModel),
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  venomEnv: state.venomEnv,
  edmundsEnv: state.edmundsEnv,
  isCapOneBypassApiGateway: !!state.featureFlags?.capOneBypassApiGateway,
  isCapOneAppDisable: !!state.featureFlags?.capOneAppDisable,
});

export const FinancingDecorator = connect(mapStateToProps)(connectToModel(FinancingDecoratorUI, stateToPropsConfig));
FinancingDecorator.preload = store => {
  const preloader = new ModelPreloader(store);
  preloader.resolve('rttSegment', VisitorModel);

  return preloader.load();
};
